import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserLostItems, getUserFoundItems, getClaimRequests } from '../services/itemService';
import { LostItem, FoundItem, ClaimRequest } from '../types';
import ItemCard from '../components/ItemCard';

type Tab = 'lost' | 'found' | 'claims';

const MyItemsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('lost');
  const [lostItems, setLostItems] = useState<LostItem[]>([]);
  const [foundItems, setFoundItems] = useState<FoundItem[]>([]);
  const [claims, setClaims] = useState<ClaimRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const [lost, found, userClaims] = await Promise.all([
        getUserLostItems(currentUser.uid),
        getUserFoundItems(currentUser.uid),
        getClaimRequests(currentUser.uid),
      ]);
      setLostItems(lost);
      setFoundItems(found);
      setClaims(userClaims);
    } catch (error) {
      console.error('Error fetching user items:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'lost', label: 'Lost Reports', count: lostItems.length },
    { key: 'found', label: 'Found Reports', count: foundItems.length },
    { key: 'claims', label: 'My Claims', count: claims.length },
  ];

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Items</h1>
          <p className="text-gray-600">View items you've reported and claims you've made</p>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === tab.key ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : (
          <>
            {activeTab === 'lost' && (
              lostItems.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {lostItems.map((item) => (
                    <ItemCard key={item.id} item={item} type="lost" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg">
                  <p className="text-gray-500 text-lg">You haven't reported any lost items</p>
                  <Link to="/report/lost" className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
                    Report a lost item →
                  </Link>
                </div>
              )
            )}

            {activeTab === 'found' && (
              foundItems.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {foundItems.map((item) => (
                    <ItemCard key={item.id} item={item} type="found" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg">
                  <p className="text-gray-500 text-lg">You haven't reported any found items</p>
                  <Link to="/report/found" className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
                    Report a found item →
                  </Link>
                </div>
              )
            )}

            {activeTab === 'claims' && (
              claims.length > 0 ? (
                <div className="space-y-4">
                  {claims.map((claim) => (
                    <div key={claim.id} className="bg-white rounded-lg shadow-md p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{claim.description}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {claim.itemType === 'lost' ? 'Lost' : 'Found'} item claim
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(claim.status)}`}>
                          {claim.status}
                        </span>
                      </div>
                      {claim.notes && (
                        <p className="mt-2 text-sm text-gray-600 border-t pt-2">{claim.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg">
                  <p className="text-gray-500 text-lg">You haven't made any claims yet</p>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyItemsPage;
